import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Info, Shuffle, Trophy, CheckCircle, Copy, ArrowRight } from 'lucide-react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { Link } from 'react-router-dom';
import { blinksApi, getBaseUrl } from '../services/blinksApi';

interface DropTier {
  chanceBps: number; // Chance in basis points (10000 = 100%)
  tokenAmount: number; // Token payout amount
}

const CreateCase: React.FC = () => {
  const { publicKey, connected, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [form, setForm] = useState({
    name: '',
    hasJackpot: false,
    jackpotTierIndex: 0,
  });
  const [tiers, setTiers] = useState<DropTier[]>([
    { chanceBps: 0, tokenAmount: 0 }, // Start with 1 tier with 0s
  ]);
  // Local state for input values to prevent focus loss
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  const [calculatedEntryCost, setCalculatedEntryCost] = useState<number | null>(null);
  const [calculatedRTP, setCalculatedRTP] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ casePda: string; blinkUrl: string; previewUrl: string; boxName: string } | null>(null);

  // Hide HeroSection when success is shown
  useEffect(() => {
    if (success) {
      document.body.classList.add('show-success');
    } else {
      document.body.classList.remove('show-success');
    }
    return () => {
      document.body.classList.remove('show-success');
    };
  }, [success]);

  // Calculate entry cost whenever tiers or jackpot settings change
  useEffect(() => {
    const calculateStats = async () => {
      // Validate total chance
      const totalChance = tiers.reduce((sum, tier) => sum + tier.chanceBps, 0);
      if (totalChance !== 10000) {
        setCalculatedEntryCost(null);
        setCalculatedRTP(null);
        return;
      }

      // Calculate average payout
      let averagePayout = 0;
      tiers.forEach((tier, index) => {
        const chance = tier.chanceBps / 10000;
        // Jackpot tier uses its own tokenAmount as the base
        averagePayout += chance * tier.tokenAmount;
      });

      // Entry cost = average_payout / 0.96 (accounts for 4% fees, results in 96% RTP)
      const entryCost = Math.ceil(averagePayout / 0.96);
      const rtp = (averagePayout / entryCost) * 100;

      setCalculatedEntryCost(entryCost);
      setCalculatedRTP(rtp);
    };

    calculateStats();
  }, [tiers, form.hasJackpot, form.jackpotTierIndex]);

  const addTier = () => {
    if (tiers.length >= 10) {
      setError('Maximum 10 tiers allowed');
      return;
    }
    setTiers([...tiers, { chanceBps: 0, tokenAmount: 0 }]);
  };

  const removeTier = (index: number) => {
    if (tiers.length <= 1) {
      setError('At least 1 tier is required');
      return;
    }
    const newTiers = tiers.filter((_, i) => i !== index);
    setTiers(newTiers);
    // Adjust jackpot tier index if needed
    if (form.hasJackpot && form.jackpotTierIndex >= newTiers.length) {
      setForm({ ...form, jackpotTierIndex: newTiers.length - 1 });
    }
  };

  const updateTier = (index: number, field: keyof DropTier, value: number) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setTiers(newTiers);
    setError(null);
  };

  const randomizeTiers = () => {
    // 1. Determine number of tiers (3 to 6)
    const numTiers = Math.floor(Math.random() * 4) + 3;

    // 2. Generate random chances (normalized to 100%)
    // Strategy: Generate random weights, sort descending so first tier is most common
    let weights = Array.from({ length: numTiers }, () => Math.random() * 10 + 1);
    weights.sort((a, b) => b - a); // Higher weights first
    
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    const chancesBps = weights.map(w => Math.floor((w / totalWeight) * 10000));

    // Fix rounding to ensure exactly 10000
    const currentSum = chancesBps.reduce((a, b) => a + b, 0);
    const diff = 10000 - currentSum;
    chancesBps[0] += diff;

    // 3. Generate payouts
    // Payouts should generally increase as chance decreases
    const newTiers: DropTier[] = chancesBps.map((chance, i) => {
      let amount;
      // Exponential-ish scaling for payouts
      if (i === 0) {
        // Common: 50 - 200 tokens
        amount = Math.floor(Math.random() * 150) + 50; 
      } else if (i === 1) {
        // Uncommon: 300 - 1000
        amount = Math.floor(Math.random() * 700) + 300;
      } else if (i === 2) {
        // Rare: 1500 - 5000
        amount = Math.floor(Math.random() * 3500) + 1500;
      } else if (i === 3) {
        // Epic: 8000 - 20000
        amount = Math.floor(Math.random() * 12000) + 8000;
      } else {
        // Legendary: 25000 - 100000
        amount = Math.floor(Math.random() * 75000) + 25000;
      }

      // Round nicely
      if (amount > 1000) amount = Math.round(amount / 100) * 100;
      else amount = Math.round(amount / 10) * 10;

      return {
        chanceBps: chance,
        tokenAmount: amount
      };
    });

    setTiers(newTiers);
    setInputValues({}); // Clear input cache
    setError(null);
  };

  const getInputValue = (index: number, field: 'chanceBps' | 'tokenAmount'): string => {
    const key = `${index}-${field}`;
    if (inputValues[key] !== undefined) {
      return inputValues[key];
    }
    const tier = tiers[index];
    if (!tier) return '';
    if (field === 'chanceBps') {
      return tier.chanceBps === 0 ? '' : (tier.chanceBps / 100).toString();
    }
    return tier.tokenAmount === 0 ? '' : tier.tokenAmount.toString();
  };

  const handleInputChange = (index: number, field: 'chanceBps' | 'tokenAmount', value: string) => {
    const key = `${index}-${field}`;
    setInputValues({ ...inputValues, [key]: value });
    
    if (field === 'chanceBps') {
      const percent = parseFloat(value) || 0;
      updateTier(index, 'chanceBps', Math.round(percent * 100));
    } else {
      const amount = parseInt(value) || 0;
      updateTier(index, 'tokenAmount', amount);
    }
  };

  const handleInputBlur = (index: number, field: 'chanceBps' | 'tokenAmount') => {
    const key = `${index}-${field}`;
    const newInputValues = { ...inputValues };
    delete newInputValues[key];
    setInputValues(newInputValues);
  };

  const totalChance = tiers.reduce((sum, tier) => sum + tier.chanceBps, 0);
  const chanceError = totalChance !== 10000;

  const handleCreate = async () => {
    if (!connected || !publicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (!form.name || form.name.length === 0 || form.name.length > 32) {
      setError('BLOOT BOX name must be 1-32 characters');
      return;
    }

    if (chanceError) {
      setError(`Total chance must equal 100% (currently ${(totalChance / 100).toFixed(2)}%)`);
      return;
    }

    if (form.hasJackpot) {
      const jackpotTier = tiers[form.jackpotTierIndex];
      if (!jackpotTier || jackpotTier.tokenAmount < 1000) {
        setError('Jackpot tier must have at least 1000 tokens');
        return;
      }
    }

    setIsCreating(true);
    setError(null);

    try {
      // Call backend to create BLOOT BOX and get transaction
      const jackpotTier = form.hasJackpot ? tiers[form.jackpotTierIndex] : null;
      const result = await blinksApi.createCase({
        name: form.name,
        dropTable: tiers,
        jackpotBase: jackpotTier ? jackpotTier.tokenAmount : undefined,
        hasJackpotTier: form.hasJackpot,
        jackpotTierIndex: form.hasJackpot ? form.jackpotTierIndex : undefined,
        creatorWallet: publicKey.toString(),
      });

      // Sign and send transaction
      if (!signTransaction) {
        throw new Error('Wallet does not support signing transactions');
      }

      // Convert base64 to Uint8Array (browser-compatible)
      const base64String = result.transaction;
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      let transaction: Transaction;
      try {
        transaction = Transaction.from(bytes);
      } catch (e: any) {
        console.error('Error deserializing transaction:', e);
        throw new Error(`Failed to parse transaction: ${e.message}`);
      }
      
      // Verify transaction structure
      if (!transaction.instructions || transaction.instructions.length === 0) {
        throw new Error('Transaction has no instructions');
      }
      
      console.log('Transaction loaded:', {
        instructions: transaction.instructions.length,
        feePayer: transaction.feePayer?.toString(),
        recentBlockhash: transaction.recentBlockhash ? 'present' : 'missing'
      });
      
      let signedTx: Transaction;
      try {
        signedTx = await signTransaction(transaction);
      } catch (e: any) {
        console.error('Wallet signing error:', e);
        console.error('Transaction details:', {
          instructions: transaction.instructions.map(ix => ({
            programId: ix.programId.toString(),
            keys: ix.keys.length,
            dataLength: ix.data.length
          }))
        });
        throw new Error(`Wallet signing failed: ${e.message || 'Unexpected error'}`);
      }
      const signature = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
      });

      await connection.confirmTransaction(signature, 'confirmed');

      // Confirm case creation with the server
      try {
        await blinksApi.confirmCase(result.casePda, signature);
      } catch (err: any) {
        console.warn('Failed to confirm case with server:', err);
        // Continue anyway - the transaction succeeded on-chain
      }

      // Build Blink URL for the new BLOOT BOX with affiliate parameter
      const apiBaseUrl = getBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');
      const blinkUrl = `${apiBaseUrl}/api/blinks/action/${result.casePda}?affiliate=${publicKey.toString()}`;
      // Use internal preview URL
      const previewUrl = `/preview/${result.casePda}?affiliate=${publicKey.toString()}`;

      setSuccess({
        casePda: result.casePda,
        blinkUrl,
        previewUrl,
        boxName: form.name,
      });
    } catch (err: any) {
      console.error('Error creating BLOOT BOX:', err);
      setError(err.message || 'Failed to create BLOOT BOX. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="create-blootbox-section">
      {!success && (
        <div className="section-header">
          <h3>Create Your BLOOT BOX</h3>
          <p className="section-desc">
            Design your custom BLOOT BOX with unique odds and payouts. Set up a localized Jackpot to BLOOT things up!
          </p>
        </div>
      )}

      {success ? (
        <div style={{
          padding: '32px',
          background: 'linear-gradient(180deg, rgba(171, 159, 242, 0.05) 0%, rgba(10, 10, 10, 0.8) 100%)',
          border: '1px solid #ab9ff2',
          borderRadius: '12px',
          marginTop: '0',
          boxShadow: '0 0 30px rgba(171, 159, 242, 0.15)',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'rgba(171, 159, 242, 0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto',
              border: '1px solid rgba(171, 159, 242, 0.3)',
              boxShadow: '0 0 20px rgba(171, 159, 242, 0.2)'
            }}>
              <CheckCircle size={32} color="#ab9ff2" strokeWidth={3} />
            </div>
            <h4 style={{ 
              color: '#fff', 
              fontSize: '1.5rem', 
              marginBottom: '8px', 
              fontFamily: 'var(--font-display)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {success.boxName.toUpperCase()} Deployed!
            </h4>
            <p style={{ color: '#888', fontSize: '0.9rem' }}>
              Your crate is now live on the Solana blockchain.
            </p>
          </div>

          <div style={{ 
            background: 'rgba(0, 0, 0, 0.3)', 
            padding: '20px', 
            borderRadius: '8px', 
            border: '1px solid #333',
            marginBottom: '24px' 
          }}>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'bold' }}>
                BLOOT BOX PDA
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '10px 12px'
              }}>
                <code style={{
                  flex: 1,
                  color: '#ab9ff2',
                  fontSize: '0.85rem',
                  fontFamily: 'var(--font-mono)',
                  wordBreak: 'break-all'
                }}>
                  {success.casePda}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(success.casePda)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '4px',
                    marginLeft: '8px',
                    transition: 'color 0.2s'
                  }}
                  title="Copy PDA"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <div>
              <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'bold' }}>
                Blink Share URL
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '10px 12px'
              }}>
                <code style={{
                  flex: 1,
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontFamily: 'var(--font-mono)',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere'
                }}>
                  {success.blinkUrl}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(success.blinkUrl)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '4px',
                    marginLeft: '8px',
                    transition: 'color 0.2s'
                  }}
                  title="Copy Blink URL"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Link
              to={success.previewUrl}
              style={{
                width: '100%',
                padding: '16px',
                background: '#ab9ff2',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(171, 159, 242, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#9d8df1';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(171, 159, 242, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ab9ff2';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(171, 159, 242, 0.3)';
              }}
            >
              Preview & Play Now <ArrowRight size={16} />
            </Link>

            <button
              onClick={() => {
                setSuccess(null);
                setForm({ name: '', hasJackpot: false, jackpotTierIndex: 0 });
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'transparent',
                color: '#ab9ff2',
                border: '1px solid #ab9ff2',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              className="create-another-btn"
            >
              Create Another Bloot Box <Plus size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="blootbox-form">
          {/* BLOOT BOX Name */}
          <div className="form-group">
            <label>BLOOT BOX Name</label>
            <input
              type="text"
              placeholder="Blooty Box"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bloot-input"
              maxLength={32}
            />
            <small style={{ color: '#888', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
              {form.name.length}/32 characters
            </small>
          </div>

          {/* Drop Table */}
          <div className="form-group">
            <div style={{ marginBottom: '12px' }}>
              <label>Drop Table (Tiers)</label>
              <small style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginTop: '4px' }}>
                💡 Tiers are processed in order - lower random numbers check earlier tiers first. 
                Place rare tiers first to win on low rolls!
              </small>
            </div>

            {tiers.map((tier, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '12px',
                  padding: '16px',
                  background: '#0a0a0a',
                  border: form.hasJackpot && index === form.jackpotTierIndex 
                    ? '1px solid rgba(255, 215, 0, 0.5)' 
                    : '1px solid #333',
                  borderRadius: '4px',
                  boxShadow: form.hasJackpot && index === form.jackpotTierIndex 
                    ? '0 0 20px rgba(255, 215, 0, 0.05)' 
                    : 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ 
                    color: form.hasJackpot && index === form.jackpotTierIndex ? '#FFD700' : '#00FF9C', 
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Tier {index + 1}
                  </span>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {form.hasJackpot && index === form.jackpotTierIndex && (
                      <span style={{
                        padding: '4px 8px',
                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                        color: '#000',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '800',
                        boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        <Trophy size={10} strokeWidth={3} /> JACKPOT
                      </span>
                    )}
                    {tiers.length > 1 && (
                      <button
                        onClick={() => removeTier(index)}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          border: '1px solid #ff4444',
                          color: '#ff4444',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#888', display: 'block', marginBottom: '4px' }}>
                      Chance (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={getInputValue(index, 'chanceBps')}
                      onChange={(e) => handleInputChange(index, 'chanceBps', e.target.value)}
                      onBlur={() => handleInputBlur(index, 'chanceBps')}
                      className="bloot-input"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#888', display: 'block', marginBottom: '4px' }}>
                      {form.hasJackpot && index === form.jackpotTierIndex ? 'Base Payout' : 'Token Payout'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={getInputValue(index, 'tokenAmount')}
                      onChange={(e) => handleInputChange(index, 'tokenAmount', e.target.value)}
                      onBlur={() => handleInputBlur(index, 'tokenAmount')}
                      className="bloot-input"
                      style={{ width: '100%' }}
                      placeholder="Tokens"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {tiers.length < 10 && (
                <button
                  onClick={addTier}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '12px 20px',
                    background: 'rgba(0, 255, 156, 0.1)',
                    border: '1px solid #00FF9C',
                    color: '#00FF9C',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    flex: 1,
                    justifyContent: 'center'
                  }}
                >
                  <Plus size={16} /> Add Tier
                </button>
              )}
              <button
                onClick={randomizeTiers}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
                title="Generate a random balanced setup"
              >
                <Shuffle size={14} /> Randomize
              </button>
            </div>

            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: totalChance === 0 ? 'transparent' : (chanceError ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 156, 0.1)'),
              border: `1px solid ${totalChance === 0 ? '#333' : (chanceError ? '#ff4444' : '#00FF9C')}`,
              borderRadius: '4px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: totalChance === 0 ? '#ffffff' : (chanceError ? '#ffffff' : '#00FF9C'), fontSize: '0.875rem' }}>
                  Total Chance: {(totalChance / 100).toFixed(2)}%
                </span>
                {chanceError && (
                  <span style={{ color: '#ffffff', fontSize: '0.75rem' }}>
                    Must equal 100%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Jackpot Configuration */}
          <div className="form-group">
            <div 
              onClick={() => setForm({ ...form, hasJackpot: !form.hasJackpot })}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                background: form.hasJackpot ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                border: form.hasJackpot ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid #333',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '16px',
                transition: 'all 0.2s ease',
                boxShadow: form.hasJackpot ? '0 4px 20px rgba(255, 255, 255, 0.05)' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: form.hasJackpot ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                  border: form.hasJackpot ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: form.hasJackpot ? '#ffffff' : '#666',
                  transition: 'all 0.2s'
                }}>
                  <Trophy size={20} />
                </div>
                <div>
                  <div style={{ 
                    fontWeight: '700', 
                    color: form.hasJackpot ? '#ffffff' : '#fff',
                    marginBottom: '4px',
                    fontSize: '0.95rem'
                  }}>
                    Add Jackpot Special Tier
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    Accumulate tokens from every play into a bonus pot
                  </div>
                </div>
              </div>
              
              {/* Toggle Switch Visual */}
              <div style={{
                width: '44px',
                height: '24px',
                background: form.hasJackpot ? '#ffffff' : '#333',
                borderRadius: '12px',
                position: 'relative',
                transition: 'background 0.2s',
                flexShrink: 0
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  background: form.hasJackpot ? '#000' : '#888',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: form.hasJackpot ? '22px' : '2px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </div>
            </div>

            {form.hasJackpot && (
              <div style={{ 
                padding: '20px', 
                background: 'rgba(255, 215, 0, 0.02)', 
                border: '1px solid rgba(255, 215, 0, 0.2)', 
                borderTop: 'none',
                marginTop: '-16px',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                animation: 'slideDown 0.3s ease-out'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '0.75rem', color: '#ffffff', display: 'block', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Select Jackpot Tier
                  </label>
                  <select
                    value={form.jackpotTierIndex}
                    onChange={(e) => setForm({ ...form, jackpotTierIndex: parseInt(e.target.value) })}
                    className="bloot-input"
                    style={{ 
                      width: '100%',
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: '#fff',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      padding: '12px',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  >
                    {tiers.map((_, index) => (
                      <option 
                        key={index} 
                        value={index}
                        style={{ background: '#1a1a1a', color: '#fff' }}
                      >
                        Tier {index + 1} ({tiers[index].chanceBps / 100}% chance)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <small style={{ color: '#aaa', fontSize: '0.75rem', display: 'flex', gap: '6px', alignItems: 'start' }}>
                    <Info size={14} style={{ flexShrink: 0, marginTop: '2px', color: '#FFD700' }} />
                    <span>
                      The jackpot base uses the selected tier's token payout amount. 
                      Bonus accumulates from 1% of each play on top of the base.
                    </span>
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* Calculated Stats */}
          {calculatedEntryCost !== null && (
            <div style={{
              padding: '16px',
              background: '#0a0a0a',
              border: '1px solid #00FF9C',
              borderRadius: '4px',
              marginBottom: '24px'
            }}>
              <h4 style={{ color: '#00FF9C', marginBottom: '12px', fontSize: '0.875rem', textTransform: 'uppercase' }}>
                Calculated Statistics
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '4px' }}>
                    Entry Cost <span style={{ fontSize: '0.7rem', color: '#666' }}>(Auto-calculated)</span>
                  </div>
                  <div style={{ color: '#00FF9C', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {calculatedEntryCost.toLocaleString()} $BLOOT
                  </div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '4px' }}>RTP (Return to Player)</div>
                  <div style={{ color: calculatedRTP && calculatedRTP > 96 ? '#00FF9C' : '#FFD700', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {calculatedRTP?.toFixed(2)}%
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '12px', padding: '8px', background: '#000', borderRadius: '4px' }}>
                <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '4px' }}>Fee Breakdown</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', gap: '16px' }}>
                  <span>Creator: 1%</span>
                  <span>Affiliate: 1%</span>
                  <span>Protocol: 1%</span>
                  <span>Jackpot: 1%</span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(255, 0, 0, 0.1)',
              border: '1px solid #FF0000',
              borderRadius: '4px',
              color: '#FF0000',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleCreate}
            className="deploy-btn"
            disabled={isCreating || !connected || !form.name || chanceError || calculatedEntryCost === null}
          >
            {isCreating ? 'CREATING BLOOT BOX...' : 'CREATE BLOOT BOX'}
          </button>

          {!connected && (
            <p style={{ marginTop: '12px', color: '#888', fontSize: '0.875rem', textAlign: 'center' }}>
              Please connect your wallet to create a BLOOT BOX
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateCase;
